import { AlertTriangle, Clock, MapPin } from 'lucide-react';
import Modal from './Modal';

// Tipo de conflicto tal cual lo devuelve la función de detección
// monitor_schedule_conflicts (mig 162, C-0). `date` solo viene en la variante
// batch monitor_schedule_conflicts_for_group (caso lote, C-3).
export type ConflictType = 'time' | 'sede' | 'both';

export interface ScheduleConflict {
  session_id: string;
  conflict_type: ConflictType;
  session_label: string;
  time_start: string; // 'HH:MM:SS'
  time_end: string; // 'HH:MM:SS'
  sede_name: string | null;
  date?: string | null; // 'YYYY-MM-DD' (solo en el caso lote)
}

interface ConflictWarningModalProps {
  open: boolean;
  conflicts: ScheduleConflict[];
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  confirmLabel?: string;
  // false = la comprobación no se pudo hacer (la lectura del contenedor o la
  // RPC fallaron). Una lista vacía y «no se ha podido mirar» se pintaban igual:
  // como que no hay choques. Default true para no cambiar a quien ya lo usa.
  comprobado?: boolean;
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

// Formateo local (sin acoplar el componente a helpers de una página): la fecha
// llega como 'YYYY-MM-DD' y la pintamos "martes 04/08".
function formatConflictDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  return `${DIAS[dt.getDay()]} ${dd}/${mm}`;
}

const hhmm = (t: string) => t.slice(0, 5);

function conflictTag(type: ConflictType): { label: string; icon: typeof Clock } {
  if (type === 'sede') return { label: 'Otra sede', icon: MapPin };
  if (type === 'both') return { label: 'Horario + sede', icon: AlertTriangle };
  return { label: 'Horario', icon: Clock };
}

// Frase clara y diferenciada por tipo de conflicto.
function describeConflict(c: ScheduleConflict): string {
  const rango = `${hhmm(c.time_start)}–${hhmm(c.time_end)}`;
  const cuando = c.date ? ` el ${formatConflictDate(c.date)}` : '';
  const sede = c.sede_name ?? 'otra sede';
  if (c.conflict_type === 'sede') {
    return `${c.session_label}: ese día ya tiene sesión en otra sede (${sede})${cuando} a las ${hhmm(c.time_start)}.`;
  }
  if (c.conflict_type === 'both') {
    return `${c.session_label} se solapa en horario y además es en otra sede (${sede}) — ${rango}${cuando}.`;
  }
  return `${c.session_label} se solapa con ${rango}${cuando}.`;
}

// Modal de aviso REUTILIZABLE (C-1): recibe la lista de conflictos ya detectada
// por quien lo invoca (no consulta la BD) y deja al admin decidir. NO bloquea:
// "Crear de todas formas" ejecuta onConfirm. Lo reusan C-2 (puntual), C-3 (lote)
// y, más adelante, "mover sesión".
export default function ConflictWarningModal({
  open,
  conflicts,
  onClose,
  onConfirm,
  title = 'Posible incompatibilidad de horario',
  confirmLabel = 'Crear de todas formas',
  comprobado = true,
}: ConflictWarningModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={comprobado ? title : 'No se ha podido comprobar el horario'}
      size="md"
    >
      <div className="flex gap-3 mb-4">
        <AlertTriangle size={20} className="text-warning flex-shrink-0 mt-0.5" />
        {comprobado ? (
          <p className="text-text-secondary text-sm font-inter">
            Este entrenador ya tiene {conflicts.length}{' '}
            {conflicts.length === 1 ? 'sesión' : 'sesiones'} que podrían chocar. Revísalo antes de
            continuar:
          </p>
        ) : (
          <p
            className="text-text-secondary text-sm font-inter"
            data-testid="conflict-sin-comprobar"
          >
            No se ha podido comprobar si el entrenador ya tiene algo a esa hora. Esto{' '}
            <strong className="text-white">no</strong> significa que esté libre: significa que la
            consulta ha fallado. Compruébalo en su agenda antes de continuar.
          </p>
        )}
      </div>

      {/* Sin comprobar no hay nada que listar: la lista vacía era justo lo que
          se leía como «no hay choques». */}
      <ul className="space-y-2 mb-6" data-testid="conflict-list" hidden={!comprobado}>
        {conflicts.map((c) => {
          const tag = conflictTag(c.conflict_type);
          const TagIcon = tag.icon;
          return (
            <li
              key={c.session_id}
              data-testid="conflict-item"
              data-conflict-type={c.conflict_type}
              className="flex items-start gap-2 rounded-lg border border-warning/20 bg-warning/5 p-3"
            >
              <TagIcon size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <span className="inline-block text-[11px] uppercase tracking-wider text-warning font-inter mb-0.5">
                  {tag.label}
                </span>
                <p className="text-text-secondary text-sm font-inter">{describeConflict(c)}</p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-ghost" data-testid="conflict-cancel">
          Cancelar
        </button>
        <button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className="btn-gold"
          data-testid="conflict-confirm"
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
